import { Button } from '@mui/material';
import { BusinessAccountTypeForm } from 'components/DAC7/BusinessAccountTypeForm';
import { BusinessIcon } from 'components/DAC7/BusinessIcon';
import { Dac7FormConfirmationDialog } from 'components/DAC7/Dac7FormConfirmationDialog';
import { PrivatePersonAccountTypeForm } from 'components/DAC7/PrivatePersonAccountTypeForm';
import { UserAccountTypeButton } from 'components/DAC7/UserAccountTypeButton';
import { UserIcon } from 'components/DAC7/UserIcon';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import BlockTitle from 'components/layout/BlockTitle/BlockTitle';
import A from 'components/Link/Link';
import withAuth from 'components/with-auth/with-auth';
import { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_DAC7_ACTION } from 'store/actions/user-dac7.actions';
import { useAppSelector } from 'store/hooks';
import { CountryPropertyType, IBusinessEntityData, IPrivatePersonData, UserAccountType } from 'types/user.types';

const DAC7 = () => {
  const { t } = useTranslation();

  const { accountType, data, errors, canSubmit } = useAppSelector(({ userData }) => {
    const { accountType, isTouched, isValid } = userData.userDac7Form;

    return {
      ...userData.userDac7Form,
      canSubmit: isValid[accountType] && isTouched[accountType],
    };
  });

  const handleData = (event: ChangeEvent<HTMLInputElement>, valueRaw?: string, fieldName?: string) => {
    const field = event?.target?.dataset?.field || fieldName;
    const value = event?.target?.value || valueRaw;

    if (!field) {
      return;
    }

    if ([CountryPropertyType.BUSINESS, CountryPropertyType.PRIVATE_PERSON].includes(field as any)) {
      USER_DAC7_ACTION.setInformation(field as keyof IBusinessEntityData | keyof IPrivatePersonData, valueRaw);

      return;
    }

    USER_DAC7_ACTION.setInformation(field as keyof IBusinessEntityData | keyof IPrivatePersonData, value);
  };

  useEffect(() => {
    USER_DAC7_ACTION.prefillForm();
    accountType && USER_DAC7_ACTION.setAccountType(accountType);
  }, []);

  return (
    <>
      <Header>
        <HeaderIconLogo icon="back" showLogo={false}></HeaderIconLogo>
      </Header>

      <div className="DAC7-page mobile-full-width">
        <BlockTitle className="DAC7-title">{t('pages.DAC7.content.title')}</BlockTitle>
        <p className="legal-basis-text">
          {t('pages.DAC7.content.legal-basis-for-request')}
          <A href={t('pages.DAC7.content.info-url')} key="" aria-label="" target="_blank" rel="noopener noreferrer">
            {' '}
            {t('pages.faq.selling-is-simple.learn-more')} ›
          </A>
        </p>

        <div className="DAC7-form">
          <h5 className="DAC7-form__account-type-title">{t('pages.DAC7.form.account-type.label')}</h5>

          <div className="DAC7-form__account-type-section">
            <UserAccountTypeButton
              icon={<UserIcon />}
              title={t('pages.DAC7.form.account-type.private-person')}
              subTitle={t('pages.DAC7.form.account-type.private-person-description')}
              active={accountType === UserAccountType.PRIVATE_PERSON}
              onClick={() => USER_DAC7_ACTION.setAccountType(UserAccountType.PRIVATE_PERSON)}
            />
            <UserAccountTypeButton
              icon={<BusinessIcon />}
              title={t('pages.DAC7.form.account-type.business')}
              subTitle={t('pages.DAC7.form.account-type.business-description')}
              active={accountType === UserAccountType.BUSINESS}
              onClick={() => USER_DAC7_ACTION.setAccountType(UserAccountType.BUSINESS)}
            />
          </div>

          {!!accountType && (
            <>
              {accountType === UserAccountType.PRIVATE_PERSON ? (
                <PrivatePersonAccountTypeForm
                  handleData={handleData}
                  errors={errors[UserAccountType.PRIVATE_PERSON]}
                  data={data[UserAccountType.PRIVATE_PERSON] as IPrivatePersonData}
                />
              ) : (
                <BusinessAccountTypeForm
                  handleData={handleData}
                  errors={errors[UserAccountType.BUSINESS]}
                  data={data[UserAccountType.BUSINESS] as IBusinessEntityData}
                />
              )}
              <p className="DAC7-form__terms-agreement">{t('pages.DAC7.form.terms-agreement')}</p>
              <Button
                size="large"
                variant="contained"
                color="primary"
                fullWidth
                onClick={USER_DAC7_ACTION.openConfirmation}
                className="profile-save-btn"
                disabled={!canSubmit}
              >
                {t('pages.DAC7.form.send')}
              </Button>
            </>
          )}
        </div>
      </div>
      {accountType && canSubmit && <Dac7FormConfirmationDialog />}
    </>
  );
};

export default withAuth(DAC7);
